import { Link, useParams } from 'react-router-dom'
import {
    AssetImage,
    EloBadge,
    RuneIcons,
    SpellIcons,
    ToneBadge,
    getToneStyles,
} from './match/MatchSharedElements'
import { blackButtonSurfaceClass, buttonFocusRingClass } from '../shared/buttonStyles'
import { assetUrl } from '../helper_functions/utilities'

function toChampionIconUrl(championNameId) {
    return assetUrl(`/champion_icons/${championNameId}.png`)
}

function toSpellIconUrl(spellId) {
    return assetUrl(`/summoner_spell_icons/${spellId}.png`)
}

function toPrimaryRuneUrl(primaryStyleId, keystoneId) {
    return assetUrl(`/major_and_minor_rune_icons/trees/${primaryStyleId}/slot_1/${keystoneId}.png`)
}

function toSecondaryRuneUrl(styleId) {
    return assetUrl(`/major_and_minor_rune_icons/trees/${styleId}.png`)
}

function formatElapsed(seconds) {
    const normalizedSeconds = Math.max(0, Math.floor(Number(seconds || 0)))
    const hours = Math.floor(normalizedSeconds / 3600)
    const minutes = Math.floor((normalizedSeconds % 3600) / 60)
    const remainingSeconds = normalizedSeconds % 60

    if (hours > 0) {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
    }

    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
}

function formatLiveIssueMessage(message, { stale = false } = {}) {
    const normalizedMessage = String(message || '').toLowerCase()

    if (
        normalizedMessage.includes('riot live-game services are temporarily unavailable') ||
        normalizedMessage.includes('bad gateway') ||
        normalizedMessage.includes('gateway') ||
        normalizedMessage.includes('cloudflare') ||
        normalizedMessage.includes('upstream')
    ) {
        return stale
            ? 'Riot servers are failing to send fresh live-game data right now. Retrying automatically while this snapshot stays visible.'
            : 'Riot servers are failing to send live-game data right now. The automatic retry may resolve it shortly.'
    }

    if (normalizedMessage.includes('rate-limit')) {
        return stale
            ? 'Live-game refresh is being rate-limited. Keeping the last successful snapshot visible while retries continue.'
            : 'Live-game checks are being rate-limited right now. Please try again shortly.'
    }

    return message || 'Unable to load live game right now.'
}

const LIVE_POSITION_ORDER = {
    TOP: 0,
    JUNGLE: 1,
    MIDDLE: 2,
    BOTTOM: 3,
    UTILITY: 4,
}

function normalizeLivePosition(position) {
    const normalizedPosition = String(position || '').trim().toUpperCase()

    if (normalizedPosition === 'MID') {
        return 'MIDDLE'
    }

    if (normalizedPosition === 'BOT' || normalizedPosition === 'ADC') {
        return 'BOTTOM'
    }

    if (normalizedPosition === 'SUPPORT') {
        return 'UTILITY'
    }

    if (normalizedPosition === 'JG') {
        return 'JUNGLE'
    }

    return LIVE_POSITION_ORDER[normalizedPosition] !== undefined ? normalizedPosition : ''
}

function getParticipantLivePosition(participant) {
    return normalizeLivePosition(
        participant?.position ||
        participant?.team_position ||
        participant?.teamPosition ||
        participant?.individual_position ||
        participant?.individualPosition ||
        participant?.assigned_position ||
        participant?.assignedPosition ||
        participant?.role
    )
}

function sortLiveParticipants(participants) {
    return [...(participants || [])].sort((leftParticipant, rightParticipant) => {
        const leftPosition = getParticipantLivePosition(leftParticipant)
        const rightPosition = getParticipantLivePosition(rightParticipant)
        const leftOrder = LIVE_POSITION_ORDER[leftPosition] ?? 99
        const rightOrder = LIVE_POSITION_ORDER[rightPosition] ?? 99

        if (leftOrder !== rightOrder) {
            return leftOrder - rightOrder
        }

        if (leftParticipant?.is_focus !== rightParticipant?.is_focus) {
            return leftParticipant?.is_focus ? -1 : 1
        }

        const leftName = String(leftParticipant?.game_name || '').toLowerCase()
        const rightName = String(rightParticipant?.game_name || '').toLowerCase()
        const nameComparison = leftName.localeCompare(rightName)

        if (nameComparison !== 0) {
            return nameComparison
        }

        return String(leftParticipant?.tag_line || '').toLowerCase().localeCompare(String(rightParticipant?.tag_line || '').toLowerCase())
    })
}

function ReturnToMatchesButton({ onClick }) {
    if (!onClick) {
        return null
    }

    return (
        <button
            type="button"
            className={`inline-flex h-8 items-center justify-center rounded-md px-3 text-[11px] font-bold uppercase tracking-[0.16em] transition-colors ${buttonFocusRingClass} ${blackButtonSurfaceClass}`}
            onClick={onClick}
        >
            Back to matches
        </button>
    )
}

function LiveParticipantRow({ championNameIdById, participant, tone }) {
    const { displayRegion } = useParams()
    const toneStyles = getToneStyles(tone)
    const championNameId = championNameIdById[participant?.champion_id] || 'Unknown'
    const rowSurface = participant?.is_focus ? toneStyles.rowHighlightSurface : toneStyles.rowSurface
    const linkPath = participant?.link_slug ? `/summoners/${displayRegion}/${participant.link_slug}` : ''

    const nameBlock = (
        <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-1.5">
                <div className="truncate text-sm font-semibold text-zinc-100">
                    {participant?.game_name || 'Unknown Player'}
                    {participant?.tag_line ? <span className="ml-1 text-zinc-300">#{participant.tag_line}</span> : null}
                </div>
                {participant?.is_focus ? (
                    <div className="rounded-sm border border-cyan-700/35 bg-cyan-900/15 px-1.5 py-[2px] text-[9px] font-bold uppercase tracking-[0.14em] text-cyan-200">
                        You
                    </div>
                ) : null}
                {participant?.bot ? (
                    <div className="rounded-sm border border-zinc-700 bg-zinc-900 px-1.5 py-[2px] text-[9px] font-bold uppercase tracking-[0.14em] text-zinc-300">
                        Bot
                    </div>
                ) : null}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <EloBadge
                    abbreviate
                    badge={participant?.elo_badge}
                    className="text-[10px]"
                    contentClassName="text-[10px]"
                    iconClassName="mr-0.5 h-[18px] w-[18px]"
                    showIcon
                />
                {participant?.league_points ? (
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-300">
                        {participant.league_points} LP
                    </div>
                ) : null}
            </div>
        </div>
    )

    return (
        <div className={`rounded-lg border px-2.5 py-2 ${rowSurface}`}>
            <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2.5">
                    <div className={`h-11 w-11 shrink-0 overflow-hidden rounded-full border-2 ${toneStyles.border}`}>
                        <AssetImage
                            alt="champion icon"
                            className="h-full w-full scale-[1.28] object-cover"
                            src={toChampionIconUrl(championNameId)}
                        />
                    </div>
                    <div className="flex shrink-0 flex-col gap-0.5">
                        <SpellIcons
                            size={18}
                            sources={[toSpellIconUrl(participant?.spell_one_id), toSpellIconUrl(participant?.spell_two_id)]}
                            tone={tone}
                        />
                        <RuneIcons
                            primaryClassName="scale-[1.08] object-cover"
                            secondaryClassName="object-contain"
                            secondaryInsetClassName="scale-[0.78] p-[1px]"
                            size={18}
                            sources={{
                                primary: toPrimaryRuneUrl(participant?.primary_style_id, participant?.keystone_id),
                                secondary: toSecondaryRuneUrl(participant?.secondary_style_id),
                            }}
                            tone={tone}
                        />
                    </div>
                    {linkPath ? (
                        <Link className="min-w-0" to={linkPath}>
                            {nameBlock}
                        </Link>
                    ) : (
                        nameBlock
                    )}
                </div>
            </div>
        </div>
    )
}

function LiveTeamCard({ bans, championNameIdById, label, participants, tone }) {
    return (
        <section className="rounded-xl border border-zinc-800 bg-zinc-950 p-3">
            <div className="flex items-center justify-between gap-3">
                <ToneBadge label={label} tone={tone} />
                {bans?.length ? (
                    <div className="flex items-center gap-1">
                        {bans.map((ban) => {
                            const championNameId = championNameIdById[ban?.champion_id] || 'Unknown'

                            return (
                                <div className="h-7 w-7 overflow-hidden rounded-full border border-zinc-700 bg-zinc-900" key={`${ban.team_id}-${ban.pick_turn}-${ban.champion_id}`}>
                                    <AssetImage
                                        alt="banned champion icon"
                                        className="h-full w-full scale-[1.22] object-cover grayscale"
                                        src={toChampionIconUrl(championNameId)}
                                    />
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">No bans shown</div>
                )}
            </div>

            <div className="mt-3 space-y-2">
                {participants.map((participant) => (
                    <LiveParticipantRow championNameIdById={championNameIdById} key={`${participant?.team_id}-${participant?.puuid || participant?.game_name}`} participant={participant} tone={tone} />
                ))}
            </div>
        </section>
    )
}

const LiveGamePanel = ({ error, liveGameData, loading, onReturnToMatches, orderedChampions, platformCode, pollingStoppedReason, staleError }) => {
    const championNameIdById = {}
    orderedChampions?.forEach((champion) => {
        championNameIdById[parseInt(champion?.champion_id, 10)] = champion?.name_id
    })

    if (loading) {
        return (
            <section className="rounded-xl border border-zinc-800 bg-zinc-950 p-4 text-zinc-300">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-300">Live Game</div>
                        <div className="mt-1 text-sm font-semibold text-zinc-200">Checking current lobby state...</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">Polling</div>
                        <ReturnToMatchesButton onClick={onReturnToMatches} />
                    </div>
                </div>
            </section>
        )
    }

    if (error) {
        return (
            <section className="rounded-xl border border-rose-900/40 bg-zinc-950 p-4 text-zinc-300">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-rose-300">Live Game</div>
                    <ReturnToMatchesButton onClick={onReturnToMatches} />
                </div>
                <div className="mt-2 text-sm font-semibold text-zinc-100">Unable to load live game.</div>
                <div className="mt-1 text-sm text-zinc-300">{formatLiveIssueMessage(error)}</div>
            </section>
        )
    }

    if (!liveGameData?.active || !liveGameData?.game) {
        return (
            <section className="rounded-xl border border-zinc-800 bg-zinc-950 p-4 text-zinc-300">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-300">Live Game</div>
                    <ReturnToMatchesButton onClick={onReturnToMatches} />
                </div>
                <div className="mt-2 text-sm font-semibold text-zinc-100">No active game detected.</div>
                <div className="mt-1 text-sm text-zinc-300">This panel will refresh when you open it again or on the next poll while it stays visible.</div>
            </section>
        )
    }

    const participants = liveGameData?.game?.participants || []
    const blueSideParticipants = sortLiveParticipants(participants.filter((participant) => participant?.team_id === 100))
    const redSideParticipants = sortLiveParticipants(participants.filter((participant) => participant?.team_id === 200))
    const bans = liveGameData?.game?.bans || []

    return (
        <section className="rounded-[1.1rem] border border-zinc-800 bg-zinc-950 p-3 text-zinc-200 shadow-[0_18px_40px_-28px_rgba(0,0,0,0.95)]">
            {staleError ? (
                <div className="mb-3 rounded-lg border border-amber-900/45 bg-amber-950/30 px-3 py-2 text-[11px] font-medium text-amber-100">
                    {formatLiveIssueMessage(staleError, { stale: true })}
                </div>
            ) : null}

            {pollingStoppedReason ? (
                <div className="mb-3 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-[11px] font-medium text-zinc-300">
                    {pollingStoppedReason}
                </div>
            ) : null}

            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-zinc-800 pb-3">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <ToneBadge label="Live Game" tone="victory" />
                        <div className="text-sm font-semibold uppercase tracking-[0.08em] text-zinc-100">
                            {liveGameData?.game?.queue_label}
                        </div>
                    </div>
                    <div className="mt-1 text-xs text-zinc-300">
                        Match ID: {platformCode ? `${String(platformCode).toUpperCase()}_` : ''}{liveGameData?.game?.game_id || 'Unknown'}
                    </div>
                </div>

                <div className="text-right">
                    <div className="flex flex-col items-end gap-2">
                        <ReturnToMatchesButton onClick={onReturnToMatches} />
                        <div>
                            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-300">Game Time</div>
                            <div className="mt-1 text-lg font-semibold tracking-tight text-zinc-100">{formatElapsed(liveGameData?.game?.elapsed_seconds)}</div>
                            <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
                                Auto refresh {liveGameData?.game?.refresh_interval_seconds || 20}s
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-3 grid gap-3 lg:grid-cols-2">
                <LiveTeamCard
                    bans={bans.filter((ban) => ban?.team_id === 100)}
                    championNameIdById={championNameIdById}
                    label="Blue Side"
                    participants={blueSideParticipants}
                    tone="victory"
                />
                <LiveTeamCard
                    bans={bans.filter((ban) => ban?.team_id === 200)}
                    championNameIdById={championNameIdById}
                    label="Red Side"
                    participants={redSideParticipants}
                    tone="defeat"
                />
            </div>
        </section>
    )
}

export default LiveGamePanel
